import type { ReactNode } from 'react';
import { Activity, Brain, ClipboardList, HeartPulse, MessageSquare, Pill, Stethoscope, User } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useGlobalContext } from '@/store';

interface StepCard {
  title: string;
  desc: string;
  path: string;
  icon: ReactNode;
  badge: string;
  badgeClass: string;
}


export function Home() {
  const { patient } = useGlobalContext();
  const navigate = useNavigate();

  const steps: StepCard[] = [
    {
      title: '1. Personal Data',
      desc: 'Register or update the patient\'s basic information.',
      path: '/account',
      icon: <User size={28} />,
      badge: 'Profile',
      badgeClass: 'badge-primary',
    },
    {
      title: '2. Case Description',
      desc: 'Describe the complaint in your own words.',
      path: '/description',
      icon: <MessageSquare size={28} />,
      badge: 'Narrative',
      badgeClass: 'badge-accent',
    },
    {
      title: '3. Physical Symptoms',
      desc: 'Refine with AI-guided physical questions.',
      path: '/physical-symptom',
      icon: <Stethoscope size={28} />,
      badge: 'Physical',
      badgeClass: 'badge-success',
    },
    {
      title: '4. Mental Symptoms',
      desc: 'Select key mental and emotional symptoms.',
      path: '/mind',
      icon: <Brain size={28} />,
      badge: 'Mental',
      badgeClass: 'badge-secondary',
    },
    {
      title: '5. Symptom Selection',
      desc: 'Choose symptoms from a predefined clinical list (Intake).',
      path: '/intake',
      icon: <ClipboardList size={28} />,
      badge: 'Intake',
      badgeClass: 'badge-info',
    },
    {
      title: '6. Results',
      desc: 'Check the suggested remedy and validate symptoms.',
      path: '/results',
      icon: <Pill size={28} />,
      badge: 'Remedy',
      badgeClass: 'badge-warning',
    },
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="hero bg-base-100 rounded-box mb-6 shadow-sm">
        <div className="hero-content flex-col md:flex-row text-center md:text-left gap-6 py-8">
          <div className="bg-primary text-primary-content rounded-full p-6">
            <HeartPulse size={48} />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Welcome to Homeopathic Consultation</h1>
            <p className="py-2 text-base-content/70">
              Clinical assistant for the selection of homeopathic remedies based on a knowledge graph.
            </p>
            <div className="flex flex-wrap items-center gap-2 justify-center md:justify-start">
              <span className="badge badge-outline badge-lg">
                <User size={14} className="mr-1" />
                {patient.email || 'No patient registered'}
              </span>
              {patient.email && (
                <span className="badge badge-primary badge-lg">
                  <Activity size={14} className="mr-1" /> Active session
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {steps.map((step) => (
          <button
            key={step.path}
            type="button"
            onClick={() => navigate(step.path)}
            className="card bg-base-100 shadow-md hover:shadow-xl transition-shadow text-left"
          >
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="text-primary">{step.icon}</div>
                <span className={`badge ${step.badgeClass}`}>{step.badge}</span>
              </div>
              <h2 className="card-title mt-2">{step.title}</h2>
              <p className="text-sm text-base-content/70">{step.desc}</p>
              <div className="card-actions justify-end mt-2">
                <span className="btn btn-sm btn-primary btn-outline">Open</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
